import {Nullable, GroupResult, Message, MessageItem} from "../models";

type Msg = Message & {sent: boolean, showSender: boolean}
type Props = {items: MessageItem[], lastDate?: string};
const alphabet = "0123456789ABCDEFGHJKMNPQRSTVWXYZ-.";
export const isPositive = (v?: number) => v && Number.isFinite(v) && v > 0; export const wait = (ms: number) => new Promise((res) => setTimeout(res, ms)); const notEmpty = (v?: string | null) => typeof v === "string" && v.length > 0; const formatDate = (date: Date|number) => new Date(date).toLocaleDateString("en-US", { month: "short",
    year: "numeric",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
export const splitCase = (v: string) => v.replace(/([a-z])([A-Z])/g, "$1 $2");
export function capitalize(v: string) {
    if (v.length === 0) {
        return v;
    }
    return v.charAt(0).toUpperCase() + v.slice(1);
}
export function generateCode(length = 10): string {
    let numbers = new Uint8Array(length);
    crypto.getRandomValues(numbers);
    return numbers.reduce((acc, v) => acc + alphabet[v % alphabet.length], "");
}

function isAlphanumeric(p: {value: string; maxChar: number}) {
    const regex = /^[a-zA-Z0-9]+$/;
    return regex.test(p.value) && p.value.length >= p.maxChar;
}
export function isValidPhone(tel: string | undefined): boolean {
    return tel === undefined || (tel !== undefined &&
    /^(?:\+?[1-9]\d{0,3}[-.\s]?)?\(?(?:[0-9][-.\s]?){6,14}[0-9]\)?$/.test(tel));
}

export function isValidEmail(val: Nullable<string>) {
    return (val &&
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val));
}

enum LoadingState {None, Pending, Success, Error};

const stateChecker = {
  isNone: (state: LoadingState) => state === LoadingState.None,
  isPending: (state: LoadingState) => state === LoadingState.Pending,
  isSuccess: (state: LoadingState) => state === LoadingState.Success,
  isError: (state: LoadingState) => state === LoadingState.Error
};

enum LoadConditionState {
  Normal = "assigned",
  HardStop = "unassigned",
  tendered = "tendered",
}

enum DrawerState {
  None,
  isOpenAssignEquipment,
  isOpenUpdateEquipment,
  closeAssignEquipment,
}

// Status bgColor mapping
const statusColors: Record<string, string> = {
  normal: "#E9EFFD",
  hardstop: "#F9CBCB",
  // added: "#C6EEDB",
  // unlocked: "#FDF1EA",
  // deleted: "#F9CBCB",
  // created: "#C6EEDB",
  // changed: "#FDF1EA",
  // default: "#F9CBCB", // Fallback color
};

// Status text Color mapping
const statusTextColors: Record<string, string> = {
  normal: "#0A53BF",
  hardstop: "#E82626",
  // unlocked: "#EA702E",
  // create: "#028B4B",
  // removed: "#E82626",
  // deleted: "#E82626",
  // created: "#028B4B",
  // changed: "#EA702E",
  // default: "#E82626", // Fallback color
};

const isHistoryPage = () => window.location.pathname.includes("load-history");


function formatCurrency(val?: number): string {
    const f = Intl.NumberFormat("en-US", {style: "currency", currency: "CAD"});
    let result = "--";
    if (Number.isFinite(val)) {
        result = f.format(val!);
    }
    return result;
}

function shuffle(array: any[]) {
  const res = Array.from(array);
  for (let i = res.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [res[i], res[j]] = [res[j], res[i]];
  }
  return res;
}


const formatGroupedMessageDate = (unixEpochSeconds: number): string => {
  const requestedDate = new Date(unixEpochSeconds); // Convert seconds to ms
  const now = new Date();

  // Normalize to remove time part
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const requested = new Date(
    requestedDate.getFullYear(),
    requestedDate.getMonth(),
    requestedDate.getDate()
  );

  const diffInMs = today.getTime() - requested.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays > 1 && diffInDays < 7) {
    // Show weekday name
    return requested.toLocaleDateString("en-US", { weekday: "long" });
  }

  // Otherwise → full format like "Aug 24, 2025"
  return requested.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function formatDateDifference(timestamp: number): string {
    const now = new Date();
    const inputDate = new Date(timestamp);
    const timeDiff = now.getTime() - inputDate.getTime();
    if (isNaN(inputDate.getTime())) {
        return "Invalid date";
    }
    if (timeDiff < 86400000 && inputDate.getDate() === now.getDate()) {
        return "today";
    }
    if (timeDiff < 172800000 && inputDate.getDate() === now.getDate() - 1) {
        return "yesterday";
    }
    const daysDiff = Math.floor(timeDiff / 86400000);
    if (daysDiff < 7 && inputDate.getTime() > getStartOfWeek(now).getTime()) {
        return inputDate.toLocaleDateString("en-US", {weekday: "long"});
    }
    return inputDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
}

function getStartOfWeek(date: Date): Date {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
}


function groupReducer(id: string): (p: GroupResult, m: Message) => GroupResult {
    let lastSender: string;
    return function (p, m) {
        const label = formatDateDifference(m.timestamp);
        const read = m.readers.includes(id);
        let msg: Msg;

        msg = Object.assign({
            sent: m.senderId === id,
            showSender: (m.senderId !== id) && (lastSender !== m.senderName)
        }, m);
        lastSender = m.senderName;
        if (!read && !p.lastUnreadId) {
            p.lastUnreadId = m.id;
        }
        if (p.messages[label]) {
            p.messages[label] = p.messages[label].concat([msg]).sort(
                (m1, m2) => m1.timestamp - m2.timestamp
            );
        } else {
            p.messages[label] = [msg];
        }
        return p;
    }
}

function messageReducer(count: number, id: string) : (p: Props, m: Message) => Props {
    let hasUnread = false; // assume the list is sorted
    return function (p, m) {
        let index = p.items.length - 1;
        const label = formatDateDifference(m.timestamp);
        const read = m.readers.includes(id);
        if (label === p.lastDate) {
            if (!read) {
                index -= 1;
            }
            p.items.splice(index, 0, {type: "message", data: m})
        } else {
            p.items.splice(index, 0, {type: "message", data: m});
        }
        if (count > 0 && (!read && !hasUnread)) {
            p.items.push({type: "unread", count});
            hasUnread = true;
        }
        if (label !== p.lastDate) {
            p.items.push({type: "date", label});
            p.lastDate = label;
        }
        return p;
    };
}


const formatFullDate = (date: string) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};


function formatRelativeTime(iso: string): string {
    const now = new Date();
    const date = new Date(iso);
    const dateOnly = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
    );
    // Strip time for accurate date comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (dateOnly.getTime() === today.getTime()) {
        // Same day → show hour:min with AM/PM
        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    }
    if (dateOnly.getTime() === yesterday.getTime()) {
        return "Yesterday";
    }
    // Otherwise → show MM/dd/YY
    return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
    });
}

export const requestMessages = {
    cancellation: {
        description: "This will close the request without a decision." +
        "Recipients will see that it was cancelled by the requester.",
        title: "Cancel this request ?"
    }
}

export {
    isAlphanumeric,
    formatCurrency,
    formatDate,
    shuffle,
    formatGroupedMessageDate,
    formatFullDate,
    formatRelativeTime,
    groupReducer,
    messageReducer,
    notEmpty,
    stateChecker,
    statusColors,
    statusTextColors,
    isHistoryPage,
    LoadingState,
    LoadConditionState,
    DrawerState
};
